"use client";

import type { AnchorHTMLAttributes, ReactNode } from "react";
import { useLocale } from "next-intl";
import { Link } from "@/i18n/routing";
import { appLocalizedUrl } from "@/lib/seo/url";

type BookingLinkProps = {
  href: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

/**
 * CTA link to the booking flow.
 *
 * The booking flow now lives on the standalone app at `app.isg-express.com`
 * (the flow is at the app **root**), not on `www/angebot`. So any `/angebot`
 * href is rewritten to an **external**, locale-correct link to the app
 * (DE → root, others → `/<locale>`), preserving the query string — e.g. the
 * home hero's `?from=…&to=…` prefill, which the app flow reads on mount.
 *
 * Every other href falls through to the normal in-app next-intl `<Link>`, so
 * this is a safe drop-in replacement anywhere a CTA *might* point at booking —
 * including the shared section primitives that render Sanity-driven hrefs.
 */
export function BookingLink({ href, children, ...rest }: BookingLinkProps) {
  const locale = useLocale();
  const [path, query] = href.split("?");

  if (path === "/angebot") {
    const appHref = appLocalizedUrl(locale) + (query ? `?${query}` : "");
    return (
      <a href={appHref} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} {...rest}>
      {children}
    </Link>
  );
}
